import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";

// Default Gradient Colors for Transition
const DEFAULT_GRADIENTS = [
  ["#4ac29a", "#bdfff3"],
  ["#6ee2f5", "#6454f0"],
  ["#ff9966", "#ff5e62"],
  ["#ff512f", "#dd2476"],
  ["#654ea3", "#eaafc8"],
];

// Linear Interpolation for Smooth Color Transition
function lerpColor(color1, color2, t) {
  let c1 = color1.match(/\w\w/g).map((hex) => parseInt(hex, 16));
  let c2 = color2.match(/\w\w/g).map((hex) => parseInt(hex, 16));
  let interpolated = c1.map((start, i) => Math.round(start + t * (c2[i] - start)));
  return `#${interpolated.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

export default function AnimatedBackground({
  children,
  gradients = DEFAULT_GRADIENTS,
  transitionSpeed = 0.02,
  withBorder = true,
  useTimeBased = false, // Enable time-based animation
  timeDuration = 10, // Time duration per color transition (seconds)
}) {
  const [gradient, setGradient] = useState(gradients[0]);
  const [index, setIndex] = useState(0);
  const [colorFactor, setColorFactor] = useState(0);

  useEffect(() => {
    let animationFrameId;

    if (useTimeBased) {
      const interval = setInterval(() => {
        setColorFactor((prev) => prev + 0.02); // Increment smoothly over time

        if (colorFactor >= 1) {
          setIndex((prevIndex) => (prevIndex + 1) % gradients.length);
          setColorFactor(0);
        }
      }, (timeDuration * 1000) / 50); // Updates every ~20ms for smooth transition

      return () => clearInterval(interval);
    }

    const updateGradientOnScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const scrollFactor = window.scrollY / maxScroll;
      const newIndex = Math.floor(scrollFactor * (gradients.length - 1));
      const nextIndex = Math.min(newIndex + 1, gradients.length - 1);
      const colorLerpFactor = (scrollFactor * (gradients.length - 1)) % 1;

      setGradient([
        lerpColor(gradients[newIndex][0], gradients[nextIndex][0], colorLerpFactor),
        lerpColor(gradients[newIndex][1], gradients[nextIndex][1], colorLerpFactor),
      ]);

      animationFrameId = requestAnimationFrame(updateGradientOnScroll);
    };

    if (!useTimeBased) {
      animationFrameId = requestAnimationFrame(updateGradientOnScroll);
    }

    return () => cancelAnimationFrame(animationFrameId);
  }, [colorFactor, gradients, transitionSpeed, useTimeBased, timeDuration]);

  useEffect(() => {
    if (useTimeBased) {
      const nextIndex = (index + 1) % gradients.length;
      setGradient([
        lerpColor(gradients[index][0], gradients[nextIndex][0], colorFactor),
        lerpColor(gradients[index][1], gradients[nextIndex][1], colorFactor),
      ]);
    }
  }, [index, colorFactor, gradients, useTimeBased]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: `linear-gradient(to bottom right, ${gradient[0]}, ${gradient[1]})`,
        transition: "background 2s ease-in-out",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        border: withBorder ? "4px solid black" : "none",
        px: { xs: 1.5, sm: 0, md: 0 },
      }}
    >
      {children}
    </Box>
  );
}

// Default Props
AnimatedBackground.defaultProps = {
  gradients: DEFAULT_GRADIENTS,
  transitionSpeed: 0.02,
  useTimeBased: false,
  timeDuration: 10,
};

// Prop Types
AnimatedBackground.propTypes = {
  children: PropTypes.node.isRequired,
  gradients: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  transitionSpeed: PropTypes.number,
  withBorder: PropTypes.bool,
  useTimeBased: PropTypes.bool, // NEW: Toggle between time-based or scroll-based
  timeDuration: PropTypes.number, // NEW: Duration of color cycle (if time-based is enabled)
};
